<script lang="ts">
    import { onUnmounted, watchEffect, watch, ref, reactive, inject, computed } from 'vue';
    import vm, { JourneyNode } from '@journeys/Journeys.viewmodel';
    import { NodeType, JourneyStatus } from '@journeys/Enums';
    import { SegmentPreviewResult } from '@cwd/views/Segments/interfaces';

    // components
    import FormCheckable from '@shared/components/Form/FormCheckable.vue';
    import { SwitchStyle } from '@shared/components/Form/Form';
    import NodeDelayFields from '../Partials/NodeDelayFields.vue';
    import { onMounted } from 'vue';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { SegmentAccess } from '@cwd/Views/Segments/Segments.access';
    import { FeatureService } from '@shared/directives/Feature.service';
    import SegmentGroup from '@cwd/Views/Segments/SegmentEditor/SegmentTypes/SegmentGroup.vue';
    import SegmentsAdd from '@cwd/Views/Segments/SegmentEditor/SegmentsAdd.vue';
    import NodeSegmentContactsPreview from '@journeys/components/UserInterface/Menues/NodeSegmentContactsPreview.vue';
    import CalculateSegmentReach from '@journeys/components/UserInterface/Menues/CalculateSegmentReach.vue';
    import NodeInheritedConditions from '@journeys/components/UserInterface/Menues/NodeInheritedConditions.vue';
    import BsAlert from '@shared/components/BsAlert.vue';
    import { AlertTypes } from '@shared/components/BsAlerts';
    import { computeCurrentNodeGraphData } from '@journeys/utils/journeySerialization';

    // this is mostly for debugging, otherwise components are Anonymous
    const component = {
        name: 'Tab_NodeSettings_Data',
        disposeWatchers: () => {},
    };

    export default {
        name: component.name,
    };
</script>

<script lang="ts" setup>
    const props = defineProps<{
        node: JourneyNode;
    }>();

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const segmentAccess: SegmentAccess = injectorAccess.injector.get(SegmentAccess);
    const featureAccess: FeatureService = injectorAccess.injector.get(FeatureService);
    if (!segmentAccess.state.segmentTypes) segmentAccess.initWithoutSegmentId();

    props.node.data.segment ||= {
        Type: 'SegmentGroup',
        Bool: 1,
        Segments: [],
    };

    const currentNodeStep = ref<any>(null);
    // shared with NodeInheritedConditions and NodeSegmentContactsPreview so both avoid
    // re-running their own full path traversal for the same node
    const incomingSegments = ref<any[]>([]);
    const segmentReach = reactive({
        result: null as SegmentPreviewResult | null,
    });

    onMounted(() => {
        const recomputeSegmentData = () => {
            const graphData = computeCurrentNodeGraphData(props.node.id);

            incomingSegments.value = graphData.incomingSegments;

            // currentNodeStep is only consumed by the Task branch below
            if (props.node.data.type === NodeType.Task) {
                currentNodeStep.value = graphData.step;
            }
        };

        recomputeSegmentData();

        watch(() => props.node.data, recomputeSegmentData, { deep: true });
    });

    // once a journey is published, each Task step's computed audience is saved server-side as
    // its own Segment (JourneyStepData.SegmentId) - link straight to it, same as the old.
    // Guard on publishedStepNodeIds (captured from OperationalData at load) rather than just
    // currentNodeStep.data.SegmentId - GraphData.sequenceSteps() assigns every step a SegmentId
    // client-side for preview purposes, including nodes added after the journey was published,
    // which never got a real Segment saved server-side.
    const publishedSegmentLink = computed(() => {
        if (vm.activeJourney.Status === JourneyStatus.Draft) return null;
        if (!vm.activeJourney.publishedStepNodeIds.has(props.node.id)) return null;

        const segmentId = currentNodeStep.value?.data?.SegmentId;
        return segmentId ? '/Segments/' + segmentId : null;
    });

    onMounted(() => {
        let latest_id = props.node.id;
        let localWatcher = watchEffect(() => {
            if (latest_id === props.node.id) return;

            component.disposeWatchers();

            latest_id = props.node.id;
        });

        onUnmounted(() => {
            localWatcher();
            component.disposeWatchers();
        });
    });
</script>

<template>
    <BsAlert v-if="publishedSegmentLink" :type="AlertTypes.Info" :content="$Resources.NodesPublishedSegmentLabel" :dismissible="false">
        <template #button>
            <a class="btn btn-info btn-xs" :href="publishedSegmentLink" target="_blank" rel="noopener">
                {{ $Resources.GoToSegmentLabel }}
            </a>
        </template>
    </BsAlert>

    <template v-if="node.data.type !== NodeType.Task">
        <div class="form-group">
            <FormCheckable
                :switch-style="SwitchStyle.Standard"
                :label="$Resources.GenericDelayLabel"
                :checked="!!node.data.delay_d"
                :value="7"
                @update:modelValue="(val: any) => (node.data.delay_d = val || 0)" />
        </div>

        <template v-if="node.data.delay_d">
            <NodeDelayFields :node="node" :show-interval-label="true" />
        </template>
    </template>

    <NodeInheritedConditions :node="node" :current-node-step="currentNodeStep" :ancestor-segments="incomingSegments" />

    <h3 class="h4">{{ $Resources.NodeConditionsHeading }}</h3>

    <div class="d-flex gap-sm flex-wrap m-sm-b">
        <SegmentsAdd
            @add="
                (newVal: string) =>
                    node.data.segment.Segments.push({
                        Type: newVal,
                        Bool: 1,
                        Segments: [],
                    })
            " />
    </div>

    <div class="m-lg-b">
        <SegmentGroup :segment="node.data.segment" :segment-group="node.data.segment" :root="true" :disallow-root-level-or="true"></SegmentGroup>
    </div>

    <template v-if="node.data.type === NodeType.Task && currentNodeStep">
        <h3 class="h4">{{ $Resources.NodeConditionsResultsHeading }}</h3>

        <div class="form-group d-flex gap-sm flex-justified flex-wrap">
            <CalculateSegmentReach
                :segmentDefinition="currentNodeStep.data.SegmentDefinition"
                :nodeId="node.id"
                btn-class="text-wrap"
                @result="segmentReach.result = $event" />

            <NodeSegmentContactsPreview :segment-definition="currentNodeStep.data.SegmentDefinition" :node-id="node.id" />

            <BsAlert
                v-if="segmentReach.result"
                :type="AlertTypes.Info"
                :title="$Resources.CalculateSegmentReachHead"
                :content="$Resources.CalculateSegmentReachBody.replace('{{Count}}', segmentReach.result.Count.toString())"
                :dismissible="false" />
        </div>
    </template>
</template>
